import React from 'react';
import { toast } from 'react-toastify';
import { useRouter } from 'next/router';
import { signInWithEmailAndPassword, UserCredential } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { setUserId, logEvent } from 'firebase/analytics';
import { ZodError, z } from 'zod';
import posthog from 'posthog-js';
import { auth, firestore, loadAnalytics } from '../../../lib';
import { Button } from '../../commons';
import { Input } from '../../inputs';
import { useFirebaseUser, useUser } from '../../../hooks';
import { User, loginSchema } from '../../../models';
import RecoveryPasswordModal from './RecoveryPasswordModal';

const toastError = (message: string) =>
  toast.error(message, {
    position: 'top-right',
    autoClose: 5000,
    hideProgressBar: false,
    closeOnClick: true,
    pauseOnHover: true,
    draggable: true,
    progress: undefined,
  });

export default function SignInForm() {
  const router = useRouter();
  const [, setUser] = useUser();
  const [, setFbUser] = useFirebaseUser();
  const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    let data: z.infer<typeof loginSchema> = null;
    try {
      data = loginSchema.parse(Object.fromEntries(formData.entries()));
    } catch (err) {
      if (err instanceof ZodError) {
        return toastError(err.issues[0].message);
      }
      return toastError(
        'An error has occurred, please try again later. CODE: E11'
      );
    }
    let credentials: UserCredential = null;
    try {
      credentials = await signInWithEmailAndPassword(
        auth,
        data.email,
        data.password
      );
    } catch (err) {
      return toastError(
        'An error has occurred, please try again later. CODE: E12'
      );
    }
    try {
      const snapshot = await getDoc(
        doc(firestore, 'users', credentials.user.uid)
      );

      if (snapshot.exists()) {
        const snapshotDataUser = snapshot.data() as User;
        setUser(snapshotDataUser);
        setFbUser(credentials.user);
        setUserId(loadAnalytics(), credentials.user.uid);
        logEvent(loadAnalytics(), 'login');
        posthog?.identify(snapshotDataUser.id, {
          ...snapshotDataUser,
          password: undefined,
        });
        await router.push(
          '/request-scholarship/[...id]',
          `/request-scholarship/2`
        );
      }
    } catch (err) {
      return toastError(
        'An error has occurred, please try again later. CODE: E13'
      );
    }
  };
  const [isOpen, setOpen] = React.useState(false);
  return (
    <>
      <form method="POST" onSubmit={onSubmit} className="">
        <Input
          type="email"
          name="email"
          label="Email"
          placeholder="Email"
          required
        />
        <Input
          type="password"
          name="password"
          label="Password"
          placeholder="Password"
          required
        />
        <button
          type="button"
          onClick={(e) => {
            e.preventDefault();
            setOpen(true);
          }}
        >
          Forgot your password?
        </button>
        <Button type="submit" color="blue" className="text-center w-full mt-5">
          Sign In
        </Button>
      </form>
      <RecoveryPasswordModal isOpen={isOpen} setIsOpen={setOpen} />
    </>
  );
}
